import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Case } from '../../models/case';
import { Partner } from '../../models/partner';
import { CasesService } from '../../../services/cases.service';
import { caseStatusClass, caseStatusLabel, caseTenantPartners, daysAgoParam } from '../../utils/case-display';
import { Subject, forkJoin, of, catchError, takeUntil } from 'rxjs';

/** How far back each headline count reaches, in days (0 = since the start of today). */
interface RecencyWindow {
  label: string;
  daysBack: number;
}

const RECENCY_WINDOWS: RecencyWindow[] = [
  { label: 'Today', daysBack: 0 },
  { label: 'Last 7 days', daysBack: 6 },
  { label: 'Last 30 days', daysBack: 29 }
];

const FEED_SIZE = 10;

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit, OnDestroy {
  /** The newest cases across every tenant — no tenantId is sent, so nothing is scoped out. */
  recentCases: Case[] = [];
  counts: { label: string; total: number | null; from: string; daysBack: number }[] = [];

  loading = true;
  error: string | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private casesService: CasesService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // A count is the same query with size 1 — we only read `total` off it.
    const countRequests = RECENCY_WINDOWS.map(window =>
      this.casesService.getAllCases({
        page: 0,
        size: 1,
        sort: ['createdAt,desc'],
        createdAtFrom: daysAgoParam(window.daysBack)
      }).pipe(catchError(() => of(null)))
    );

    const feedRequest = this.casesService.getAllCases({
      page: 0,
      size: FEED_SIZE,
      sort: ['createdAt,desc']
    }).pipe(catchError(() => of(null)));

    forkJoin([feedRequest, ...countRequests]).pipe(
      takeUntil(this.destroy$)
    ).subscribe(([feed, ...countResponses]) => {
      this.recentCases = Array.isArray(feed?.data) ? feed!.data : [];

      this.counts = RECENCY_WINDOWS.map((window, index) => ({
        label: window.label,
        total: countResponses[index]?.total ?? null,
        from: daysAgoParam(window.daysBack),
        daysBack: window.daysBack
      }));

      // The rest of the page still works, so this is a banner rather than a takeover.
      if (!feed && countResponses.every(response => !response)) {
        this.error = 'Failed to load recent cases';
      }

      this.loading = false;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Opens /cases already filtered to the same window the tile counted. */
  viewCasesSince(from: string): void {
    this.router.navigate(['/cases'], { queryParams: { createdAtFrom: from } });
  }

  viewCaseDetails(caseItem: Case): void {
    if (caseItem.id) {
      this.router.navigate(['/cases', caseItem.id]);
    }
  }

  getTenantPartners(caseItem: Case): Partner[] {
    return caseTenantPartners(caseItem);
  }

  getCaseStatusClass(status?: string): string {
    return caseStatusClass(status);
  }

  getCaseStatusLabel(status?: string): string {
    return caseStatusLabel(status);
  }

  trackByCaseId(index: number, caseItem: Case): string | number {
    return caseItem.id ?? index;
  }

  trackByPartnerId(index: number, partner: Partner): string | number {
    return partner.id ?? index;
  }
}
